import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { HiX } from 'react-icons/hi'
import { useAuth } from '../../context/AuthContext'
import Button from '../ui/Button'

function Field({ label, type = 'text', value, onChange, placeholder, required }) {
  return (
    <div>
      <label className="block font-body text-sm text-ink/70 mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full px-4 py-3 rounded-xl border border-light-400 font-body text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-colors"
      />
    </div>
  )
}

function LoginForm({ onSuccess }) {
  const { login }                     = useAuth()
  const [email,    setEmail]          = useState('')
  const [password, setPassword]       = useState('')
  const [error,    setError]          = useState('')
  const [loading,  setLoading]        = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(email, password)
      onSuccess?.()
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Field label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required />
      <Field label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
      {error && <p className="text-sm font-body text-red-500">{error}</p>}
      <Button type="submit" variant="primary" className="w-full" loading={loading}>Sign In</Button>
    </form>
  )
}

function RegisterForm({ onSuccess }) {
  const { register }                  = useAuth()
  const [form,    setForm]            = useState({ first_name: '', last_name: '', email: '', password: '', phone: '' })
  const [error,   setError]           = useState('')
  const [loading, setLoading]         = useState(false)

  const set = (key) => (e) => setForm(v => ({ ...v, [key]: e.target.value }))

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await register(form)
      onSuccess?.()
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="First Name *" value={form.first_name} onChange={set('first_name')} placeholder="Sara"  required />
        <Field label="Last Name"    value={form.last_name}  onChange={set('last_name')}  placeholder="Ali" />
      </div>
      <Field label="Email *"    type="email"    value={form.email}    onChange={set('email')}    placeholder="you@example.com"    required />
      <Field label="Phone"      type="tel"      value={form.phone}    onChange={set('phone')}    placeholder="+20 100 000 0000" />
      <Field label="Password *" type="password" value={form.password} onChange={set('password')} placeholder="Min. 6 characters"  required />
      {error && <p className="text-sm font-body text-red-500">{error}</p>}
      <Button type="submit" variant="primary" className="w-full" loading={loading}>Create Account</Button>
    </form>
  )
}

export default function AuthModal() {
  const { authModal, closeAuthModal, switchMode } = useAuth()
  const { open, mode } = authModal

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="auth-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeAuthModal}
            className="fixed inset-0 bg-ink/50 backdrop-blur-sm z-[60]"
          />

          <motion.div
            key="auth-modal"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1,    y: 0  }}
            exit={{   opacity: 0, scale: 0.95, y: 20  }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="fixed inset-0 flex items-center justify-center z-[61] p-4 pointer-events-none"
          >
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md pointer-events-auto overflow-hidden">
              {/* Header */}
              <div className="px-8 pt-8 flex items-start justify-between">
                <div>
                  <h2 className="font-display text-2xl text-ink">
                    {mode === 'login' ? 'Welcome Back' : 'Create Account'}
                  </h2>
                  <p className="font-body text-sm text-ink-muted mt-1">
                    {mode === 'login' ? 'Sign in to manage your orders.' : 'Join Farafeero World.'}
                  </p>
                </div>
                <button
                  onClick={closeAuthModal}
                  className="w-9 h-9 rounded-full bg-light-200 flex items-center justify-center text-ink-muted hover:bg-light-300 transition-colors shrink-0"
                >
                  <HiX size={18} />
                </button>
              </div>

              {/* Tab switcher */}
              <div className="mx-8 mt-6 flex gap-1 bg-light-100 rounded-full p-1">
                {['login', 'register'].map((m) => (
                  <button
                    key={m}
                    onClick={() => switchMode(m)}
                    className={[
                      'flex-1 py-2 rounded-full font-body text-sm font-semibold transition-all duration-200',
                      mode === m
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-ink-muted hover:text-ink',
                    ].join(' ')}
                  >
                    {m === 'login' ? 'Sign In' : 'Register'}
                  </button>
                ))}
              </div>

              {/* Form */}
              <div className="px-8 py-6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={mode}
                    initial={{ opacity: 0, x: mode === 'login' ? -16 : 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                  >
                    {mode === 'login'
                      ? <LoginForm onSuccess={closeAuthModal} />
                      : <RegisterForm onSuccess={closeAuthModal} />
                    }
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
